import type { UserEntity } from '@backstage/catalog-model';
import { RELATION_MEMBER_OF } from '@backstage/catalog-model';
import { Link } from '@backstage/core-components';
import {
  EntityInfoCard,
  EntityRefLinks,
  getEntityRelations,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { Avatar, Flex } from '@backstage/ui';
import type { SvgIconComponent } from '@ns/plugin-ns-theme';
import {
  BusinessIcon,
  EmailIcon,
  FieldRow,
  GroupIcon,
  WorkIcon,
} from '@ns/plugin-ns-theme';

import { extractRoleFields } from './entra-role-fields';

import styles from './ns-user-profile-card.module.css';

// The Functie/Afdeling annotations are keyed by suffix; a stable icon per key
// keeps the mapping local instead of threading icon choice through the helper.
const ROLE_ICONS: Record<'department' | 'job-title', SvgIconComponent> = {
  department: BusinessIcon,
  'job-title': WorkIcon,
};

// Row spacing is owned by a single flex `gap` on the rows container rather than
// per-element padding/margins, so the icon+text rows sit evenly without the
// double padding that MUI List/ListItem and the card header/body seam introduce.
const CardTitle = (props: { pictureSrc?: string; title: string }) => (
  <Flex align="center" gap="2">
    <Avatar
      name={props.title}
      purpose="decoration"
      size="small"
      src={props.pictureSrc || ''}
    />
    {props.title}
  </Flex>
);

/**
 * NS replacement for the org `EntityUserProfileCard` body. Renders the same
 * avatar/name header plus email and group-membership rows as the framework
 * card, and additionally shows the person's Functie (job title) and Afdeling
 * (department) from the `entra.ns.nl/*` annotations, placed between email and
 * groups so the role sits with the person.
 */
export const NsUserProfileCard = () => {
  const { entity: user } = useEntity<UserEntity>();

  const profile = user.spec.profile;
  const displayName = profile?.displayName ?? user.metadata.name;
  const email = profile?.email;
  const picture = profile?.picture;
  const memberOfRelations = getEntityRelations(user, RELATION_MEMBER_OF, {
    kind: 'Group',
  });
  const roleFields = extractRoleFields(user.metadata.annotations);

  return (
    <EntityInfoCard
      title={<CardTitle pictureSrc={picture} title={displayName} />}
    >
      <div className={styles.rows}>
        {email && (
          <FieldRow icon={EmailIcon} label="Email">
            <Link to={`mailto:${email}`}>{email}</Link>
          </FieldRow>
        )}
        {roleFields.map(field => (
          <FieldRow
            key={field.key}
            icon={ROLE_ICONS[field.key]}
            label={field.label}
          >
            {field.value}
          </FieldRow>
        ))}
        <FieldRow icon={GroupIcon} label="Member of">
          <EntityRefLinks defaultKind="Group" entityRefs={memberOfRelations} />
        </FieldRow>
      </div>
    </EntityInfoCard>
  );
};
